import type { GeoTile, ApiMeta, Station, PriceHistoryFile } from '@fuelfinder/shared'

/**
 * Base URL for API requests
 * Uses relative path that works with Vite dev proxy and production
 */
const API_BASE = '/api'

/**
 * Fetch a single tile by geohash
 */
export async function fetchTile(geohash: string): Promise<GeoTile> {
  const response = await fetch(`${API_BASE}/tiles/${geohash}.json`)
  if (!response.ok) {
    // Return empty tile for 404s (tile doesn't exist for this geohash)
    if (response.status === 404) {
      return { geohash, stations: [] }
    }
    throw new Error(`Failed to fetch tile ${geohash}: ${response.status}`)
  }

  // Check Content-Type to detect SPA fallback (CloudFront returns index.html for missing tiles)
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    // SPA fallback - treat as empty tile (no stations in this geohash area)
    return { geohash, stations: [] }
  }

  return response.json()
}

/**
 * Fetch API metadata (last updated, provider status)
 */
export async function fetchMeta(): Promise<ApiMeta> {
  const response = await fetch(`${API_BASE}/meta.json`)
  if (!response.ok) {
    throw new Error(`Failed to fetch meta: ${response.status}`)
  }
  return response.json()
}

/**
 * Fetch multiple tiles in parallel and merge/dedupe stations
 * @param geohashes Array of geohash strings to fetch
 * @returns Merged array of unique stations
 */
export async function fetchAllTiles(geohashes: string[]): Promise<Station[]> {
  // Fetch all tiles in parallel
  const tiles = await Promise.all(geohashes.map(fetchTile))

  // Merge and dedupe stations by id
  const stationMap = new Map<string, Station>()
  for (const tile of tiles) {
    for (const station of tile.stations) {
      // Use station.id as key - later tiles won't overwrite earlier ones
      if (!stationMap.has(station.id)) {
        stationMap.set(station.id, station)
      }
    }
  }

  return Array.from(stationMap.values())
}

/**
 * In-memory cache for all stations data (fetched once per session)
 */
let allStationsCache: Station[] | null = null
let allStationsPromise: Promise<Station[]> | null = null

/**
 * Fetch all UK stations (cached for the session).
 * Used by UK Stats modal — ~150KB gzipped on the wire.
 */
export async function fetchAllStations(): Promise<Station[]> {
  if (allStationsCache) return allStationsCache

  if (allStationsPromise) return allStationsPromise

  allStationsPromise = (async () => {
    const response = await fetch(`${API_BASE}/all.json`)
    if (!response.ok) {
      throw new Error(`Failed to fetch all stations: ${response.status}`)
    }

    const contentType = response.headers.get('content-type') || ''
    if (!contentType.includes('application/json')) {
      return []
    }

    const data: Station[] = await response.json()
    allStationsCache = data
    return data
  })()

  try {
    return await allStationsPromise
  } catch (err) {
    allStationsPromise = null
    throw err
  }
}

/**
 * In-memory cache for history data (fetched once per session)
 */
let historyCache: PriceHistoryFile | null = null
let historyPromise: Promise<PriceHistoryFile> | null = null

/**
 * Fetch price history data (cached for the session)
 */
export async function fetchHistory(): Promise<PriceHistoryFile> {
  if (historyCache) return historyCache

  if (historyPromise) return historyPromise

  historyPromise = (async () => {
    const response = await fetch(`${API_BASE}/history.json`)
    if (!response.ok) {
      if (response.status === 404) {
        return { dates: [], stations: {} }
      }
      throw new Error(`Failed to fetch history: ${response.status}`)
    }

    const contentType = response.headers.get('content-type') || ''
    if (!contentType.includes('application/json')) {
      return { dates: [], stations: {} }
    }

    const data: PriceHistoryFile = await response.json()
    historyCache = data
    return data
  })()

  try {
    return await historyPromise
  } catch (err) {
    historyPromise = null
    throw err
  }
}
